import { toast } from 'sonner';
import { metricInfo, calculateIdealScoreRange, calculateCurrentScoreRange } from './monitoring';

// Google's official "Good" thresholds for Core Web Vitals and related metrics
// Source: https://web.dev/articles/vitals
// Note: Google uses the SAME thresholds for both mobile and desktop
// Values are stored in raw units (ms for time-based, raw value for CLS)
export const idealScoresByFormFactor = {
    mobile: {
        lcp: 2500, // ms - ≤ 2.5s is "Good" (Core Web Vital)
        cls: 0.1,  // ≤ 0.1 is "Good" (Core Web Vital)
        tti: 3800, // ms - ≤ 3.8s is "Good" (lab metric)
        fcp: 1800, // ms - ≤ 1.8s is "Good"
        ttfb: 800, // ms - ≤ 0.8s is "Good"
        tbt: 200,  // ms - < 200ms is "Good" (lab metric, correlates with INP)
    },
    // Google uses the same thresholds for desktop
    // Desktop devices typically achieve better scores due to more resources,
    // but the "good" threshold remains the same
    desktop: {
        lcp: 2500, // ms - ≤ 2.5s is "Good"
        cls: 0.1,  // ≤ 0.1 is "Good"
        tti: 3800, // ms - ≤ 3.8s is "Good"
        fcp: 1800, // ms - ≤ 1.8s is "Good"
        ttfb: 800, // ms - ≤ 0.8s is "Good"
        tbt: 200,  // ms - < 200ms is "Good"
    },
};

// Default to mobile ideal scores for backward compatibility
export const idealScores = idealScoresByFormFactor.mobile;

// Helper to get ideal scores for a specific form factor
export function getIdealScoresForFormFactor(formFactor: 'mobile' | 'desktop' = 'mobile') {
    return idealScoresByFormFactor[formFactor] || idealScoresByFormFactor.mobile;
}

const deviation = 0.15; // 15%

export const validateThresholds = (
    thresholds: { [key: string]: number },
    metricsToValidate?: string[]
) => {
    const metrics = metricsToValidate || Object.keys(thresholds);

    for (const metric of metrics) {
        const idealScore = idealScores[metric as keyof typeof idealScores];
        const threshold = thresholds[metric];

        if (idealScore === undefined || threshold === undefined) {
            continue;
        }

        const min = idealScore * (1 - deviation);
        const max = idealScore * (1 + deviation);

        if (threshold < min || threshold > max) {
            toast.error(`${metric.toUpperCase()} threshold must be between ${min.toFixed(metric === 'cls' ? 2 : 0)} and ${max.toFixed(metric === 'cls' ? 2 : 0)}.`);
            return false;
        }
    }

    return true;
};

/**
 * Validate thresholds based on current score baseline
 * Uses current ±15% range calculation
 * 
 * @param thresholds - The threshold values to validate (in raw units like ms)
 * @param currentScores - Current scan scores or null if unavailable (in raw units)
 * @param metricsToValidate - Specific metrics to validate
 * @returns true if all thresholds are valid, false otherwise
 */
export const validateThresholdsForCurrentScoreBaseline = (
    thresholds: { [key: string]: number },
    currentScores: { [key: string]: number | null } | null,
    metricsToValidate?: string[]
) => {
    const metrics = metricsToValidate || Object.keys(thresholds);

    for (const metric of metrics) {
        const idealScore = idealScores[metric as keyof typeof idealScores];
        const threshold = thresholds[metric];
        const info = metricInfo[metric as keyof typeof metricInfo];

        if (idealScore === undefined || threshold === undefined || !info) {
            continue;
        }

        const currentScore = currentScores?.[metric] ?? null;
        const { min: minDisplay, max: maxDisplay } = calculateCurrentScoreRange(currentScore, idealScore, info.conversion, metric);

        // Convert display values back to raw units for comparison
        const min = minDisplay * info.conversion;
        const max = maxDisplay * info.conversion;

        if (threshold < min || threshold > max) {
            toast.error(`${metric.toUpperCase()} threshold must be between ${min.toFixed(metric === 'cls' ? 2 : 0)} and ${max.toFixed(metric === 'cls' ? 2 : 0)}.`);
            return false;
        }
    }

    return true;
};